import React, { useMemo, useState } from 'react';
import moment from 'moment';
import parse from 'html-react-parser';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../../../../base/hooks';
import { Currency } from '../../../../../../base/interfaces/config';
import { Icon } from '../../../../../../base/components/Icon';
import { __t } from '../../../../../../base/utils/t';
import { HoverTooltip } from '../../../../../../base/components/HoverTooltip';
import { ActivityWidget } from '../../ActivityWidget';
import { getCurrencyById, mapRateTypeToString } from '../../../../../../base/utils';
import { Button } from '../../../../../../base/components/Button';
import { PopupMenu, PopupMenuOption } from '../../../../../../base/components/PopupMenu';
import { vacancyListActions, VacancyListItem } from '../../../redux/slices/vacancyListSlice';
import { CloseVacancyModal } from '../../CloseVacancyModal';

import './styles.scss';

interface Props {
    vacancyData: VacancyListItem;
    currencies: Currency[];
    upgradeVacancyHandler: (vacancyId: string) => void;
}

export const VacancyMobileListItem = ({
    vacancyData: {
        id,
        name,
        isActive,
        isDraft,
        isUpgraded,
        rateType,
        rateFrom,
        rateTo,
        currencyId,
        updatedAt,
        responses,
        city,
        recommendations,
    },
    currencies,
    upgradeVacancyHandler,
}: Props) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [showCloseModal, setShowCloseModal] = useState(false);

    const toggleCloseModalVisibility = () => setShowCloseModal(!showCloseModal);

    const topData = useMemo(() => {
        const expirationDate = moment(updatedAt).add(30, 'days');
        const isAboutToExpire = moment().startOf('day').isAfter(expirationDate.subtract(3, 'day').startOf('day'));

        return (
            <div className='vacancy-mobile-list-item__top-data'>
                <div className='vacancy-mobile-list-item__top-data-item'>{id}</div>
                <div className='vacancy-mobile-list-item__top-data-item vacancy-date'>
                    <div className='vacancy-date__date'>{moment(updatedAt).format('DD.MM.YY')}</div>

                    {isActive && (
                        <HoverTooltip
                            className='vacancy-date__tooltip vacancy-mobile-list-item__tooltip'
                            text={`${__t('Вакансия автоматически закроется')} ${moment(expirationDate).format(
                                'DD.MM.YY',
                            )}`}
                            theme='dark'
                            showArrow={true}
                        >
                            <Icon
                                icon='clock-regular'
                                color={isAboutToExpire ? 'danger' : 'info'}
                                size='14'
                                className='vacancy-date__icon'
                            />
                        </HoverTooltip>
                    )}
                </div>

                {isActive && <div className='vacancy-status-label vacancy-status-label--published vacancy-mobile-list-item__vacancy-status-label'>{__t('Открыта')}</div>}
                
                {isDraft && <div className='vacancy-status-label vacancy-status-label--unpublished vacancy-mobile-list-item__vacancy-status-label'>{__t('Чернетка')}</div>}
                
                {!isActive && !isDraft && <div className='vacancy-status-label vacancy-status-label--archived vacancy-mobile-list-item__vacancy-status-label'>{__t('Закрыта')}</div>}
            </div>
        );
    }, [id, isActive, updatedAt]);

    const responsesData = useMemo(
        () => (
            <div className='vacancy-mobile-list-item__responses-data'>
                <ActivityWidget vacancyId={id} responses={responses} className='vacancy-table__activity-widget' />
            </div>
        ),
        [responses],
    );

    const vacancyData = useMemo(() => {
        const rateString = `${rateFrom} - ${rateTo} ${
            getCurrencyById(currencies, currencyId)?.sign
        }/${mapRateTypeToString(rateType)}`;
        return (
            <div className='vacancy-mobile-list-item__vacancy-data'>
                <Link
                    to={`/vacancy/view/${id}`}
                    className='vacancy-mobile-list-item__vacancy-name'
                    title={name}
                    target='_blank'
                >
                    {isUpgraded ? <span className='vacancy-mobile-list-item__hot-label'>&#x1f525;&nbsp;</span> : ''}
                    <span className='vacancy-mobile-list-item__vacancy-name-text-elipsis'>{name}</span>
                </Link>
                <div className='vacancy-mobile-list-item__vacancy-additional-info-wrap'>
                    <div className='vacancy-mobile-list-item__vacancy-additional-info-item'>
                        {parse(city.country.flag)}
                        <span className='vacancy-mobile-list-item__city' title={city.name}>
                            {city.name}
                        </span>
                    </div>
                    <div className='vacancy-mobile-list-item__vacancy-additional-info-item'>
                        <Icon
                            icon='coins-solid'
                            color='info'
                            size='14'
                            className='vacancy-mobile-list-item__rate-icon'
                        />
                        <span className='vacancy-mobile-list-item__rate-text' title={rateString}>
                            {rateString}
                        </span>
                    </div>
                </div>
            </div>
        );
    }, [id, isUpgraded, name, rateFrom, rateTo, currencies, currencyId, rateType, city]);

    const actionsData = useMemo(() => {
        let button;
        if (isDraft || !isActive) {
            button = (
                <Button
                    onClick={() => dispatch(vacancyListActions.openVacancyRequest(id))}
                    className='vacancy-mobile-list-item__action-button'
                >
                    {__t('Открыть')}
                </Button>
            );
        }

        if(isActive && !isUpgraded) {
            button = (
                <Button
                    type='secondary'
                    onClick={() => upgradeVacancyHandler(id)}
                    className='vacancy-mobile-list-item__action-button'
                >
                    {__t('Продвигать')}
                </Button>
            );
        }

        if(isActive && isUpgraded) { 
            button = <span className='vacancy-row__premium-label'>{__t('Premium')}</span>;
        }

        let options: PopupMenuOption[] = [
            {
                icon: 'pencil-light',
                text: __t('Редактировать'),
                onClick: () => navigate(`/vacancy/update/${id}`, { state: { from: location } }),
            },
            {
                icon: 'copy-light',
                text: __t('Дублировать'),
                onClick: () => navigate(`/vacancy/duplicate/${id}`, { state: { from: location } }),
            },
        ];

        if (isActive) {
            options = [
                {
                    icon: 'user-helmet-safety-light',
                    text: `${__t('Кандидаты')} ${
                        (recommendations && recommendations.workers && recommendations.workers.count) || 0
                    } ${__t('CV')}`,
                    onClick: () => {
                        const url = recommendations && recommendations.workers && recommendations.workers.url;
                        if (url) {
                            window.location.href = url;
                        }
                    },
                },
                ...options,
                {
                    icon: 'arrow-rotate-right-light',
                    text: __t('Обновить вакансию'),
                    onClick: () => dispatch(vacancyListActions.renewVacancyRequest(id)),
                    disabled: !window.GLOBALS.canRenewVacancy,
                }
            ];
        }

        if(isActive || isDraft) {
            options = [
                ...options,
                {
                    icon: 'ban-light',
                    text: __t('Закрыть вакансию'),
                    onClick: toggleCloseModalVisibility,
                },
            ];
        }

        return (
            <div className='vacancy-mobile-list-item__actions-data'>
                {button}
                <PopupMenu options={options} align='right'>
                    <Icon icon='ellipsis-regular' size='20' color='grey' />
                </PopupMenu>
            </div>
        );
    }, [id, isActive, isUpgraded]);

    const closedOverlay = useMemo(() => {
        if (!isActive && !isDraft) {
            return <div className='vacancy-mobile-list-item__closed-overlay'></div>;
        }
        return null;
    }, [isActive]);

    return (
        <>
            <div className='vacancy-mobile-list-item'>
                {closedOverlay}
                {topData}
                {vacancyData}
                {responsesData}
                {actionsData}
            </div>
            <CloseVacancyModal
                vacancyId={id}
                visible={showCloseModal}
                actionAfterSubmit='reload'
                visibilityHandler={toggleCloseModalVisibility}
            />
        </>
    );
};
